import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { ContractModalSource } from '#src/app/pages/ambassadors/landing/pageContext';

import type { ContractFormView } from '../useContractForm';

/**
 * Wraps the modal's raw `close` action with the abandonment-vs-completion tracking event.
 *
 * Callers must wire this to `onOpenChange` (fired for Esc, backdrop click, and the X button) -
 * never to the DS `Modal`'s `onClose` prop, which fires *only* from the X button and would miss
 * Esc/backdrop entirely (and never fires at all in the thanks view, since `ModalHeader` - the only
 * place `onClose` is consumed - isn't rendered there). `onClose` must keep calling the raw `close`
 * directly, so the X button's two internal dismiss signals don't double-fire this event. The
 * thanks screen's "Done" button is a separate dismissal path (not routed through the Modal's own
 * dismiss mechanics at all, so it can't double up with `onOpenChange`) and should use this tracked
 * close too.
 */
export function useTrackedClose(
	source: ContractModalSource | null,
	view: ContractFormView,
	close: () => void,
): () => void {
	const track = useTracking();

	return useCallback(() => {
		void track('ambassadors_contract_modal_close', { source_location: source ?? undefined, view_context: view });
		close();
	}, [close, source, track, view]);
}
